// PDF de una inspección (F3a), con el mismo estilo que el PDF del plan y de los simulacros (pe-plan-pdf.ts,
// pe-simulacros-pdf.ts): portada oscura, títulos azules con línea, campos en dos columnas, encabezado y pie en todas
// las páginas, firmas en recuadros con nombre, cédula, fecha y hora y hash (K6). Contenido: datos, respuestas por
// sección (con observaciones y fotos en miniatura), hallazgos y firmas.

import { jsPDF } from 'jspdf';
import { paraPdf } from '../pe/pe-plan-pdf';
import { esFalla, estadoHallazgo, fechaEc, fechaInspeccion, itemsDe, textoRegla } from './insp-logica';
import type { FilaHallazgo, FilaInspeccion, FilaRespuesta, MetodoFirma, RolFirma } from './insp-tipos';
import {
  ETIQUETA_ESTADO_HALLAZGO, ETIQUETA_ESTADO_INSPECCION, ETIQUETA_METODO_FIRMA, ETIQUETA_RIESGO, ETIQUETA_ROL_FIRMA, ETIQUETA_VALOR,
} from './insp-tipos';

export const TITULO_INSPECCION = 'Informe de inspección';

export interface FirmaPDF { rol: RolFirma; nombre: string; cedula: string; firmadoEn: string; hash: string; imagen: string | null; archivo?: boolean }

export interface DatosInspeccionPDF {
  empresa: string;
  inspeccion: FilaInspeccion;
  sede: string;
  cantonSede: string | null;
  responsable: string;
  respuestas: FilaRespuesta[];
  fotos: Record<string, (string | null)[]>;     // por ítem: data URL de cada foto (null = no se pudo incrustar)
  hallazgos: FilaHallazgo[];
  firmas: FirmaPDF[];
  metodo: MetodoFirma | null;
  borrador: boolean;
  fecha: Date;
}

const MARGEN = 15;
const ANCHO = 210 - MARGEN * 2;
const ALTO_UTIL = 297 - 20;
const ARRIBA = 22;

const formatoImagen = (dataUrl: string) => (dataUrl.startsWith('data:image/png') ? 'PNG' : dataUrl.startsWith('data:image/webp') ? 'WEBP' : 'JPEG');

function respuestaTexto(tipo: string, r: FilaRespuesta | undefined, unidad: string | null): string {
  if (!r) return 'Sin respuesta';
  if (tipo === 'numero') return r.number_value === null || r.number_value === undefined ? 'Sin respuesta' : `${String(r.number_value).replace('.', ',')}${unidad ? ` ${unidad}` : ''}`;
  if (tipo === 'texto') return r.text_value?.trim() || 'Sin respuesta';
  if (tipo === 'foto') return 'Ver fotos';
  return r.value ? ETIQUETA_VALOR[r.value] : 'Sin respuesta';
}

export function generarInspeccionPDF(d: DatosInspeccionPDF, hoy: Date = new Date()): jsPDF {
  const i = d.inspeccion;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const T = (s: string) => paraPdf(s);
  let y = ARRIBA;
  const marca = () => {
    if (!d.borrador) return;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(80); doc.setTextColor(241, 245, 249);
    doc.text('BORRADOR', 105, 175, { align: 'center', angle: 45 });
    doc.setTextColor(15, 23, 42);
  };
  const nuevaPagina = () => { doc.addPage(); marca(); y = ARRIBA; };
  const espacio = (alto: number) => { if (y + alto > ALTO_UTIL) nuevaPagina(); };
  const texto = (s: string, tam = 9, o: { negrita?: boolean; sangria?: number; gris?: boolean; color?: [number, number, number] } = {}) => {
    doc.setFont('helvetica', o.negrita ? 'bold' : 'normal'); doc.setFontSize(tam);
    if (o.color) doc.setTextColor(...o.color); else if (o.gris) doc.setTextColor(100, 116, 139); else doc.setTextColor(15, 23, 42);
    const sangria = o.sangria ?? 0;
    (doc.splitTextToSize(T(s), ANCHO - sangria) as string[]).forEach(l => { espacio(tam * 0.45); doc.text(l, MARGEN + sangria, y); y += tam * 0.45; });
    doc.setTextColor(15, 23, 42);
  };
  const titulo = (s: string) => {
    espacio(14); y += 3;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11.5); doc.setTextColor(29, 111, 163);
    doc.text(T(s), MARGEN, y);
    doc.setDrawColor(203, 213, 225); doc.line(MARGEN, y + 1.5, MARGEN + ANCHO, y + 1.5);
    y += 6.5; doc.setTextColor(15, 23, 42);
  };
  const COL_ETIQUETA = 58, COL_VALOR = 62, LINEA = 4.2;
  const campos = (pares: [string, string][]) => {
    for (const [k, v] of pares) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
      const etiqueta = doc.splitTextToSize(T(`${k}:`), COL_ETIQUETA) as string[];
      doc.setFont('helvetica', 'normal');
      const valor = doc.splitTextToSize(T(v || '—'), ANCHO - COL_VALOR) as string[];
      const lineas = Math.max(etiqueta.length, valor.length);
      espacio(lineas * LINEA + 2);
      const y0 = y;
      doc.setFont('helvetica', 'bold');
      etiqueta.forEach((l, n) => doc.text(l, MARGEN, y0 + n * LINEA));
      doc.setFont('helvetica', 'normal');
      valor.forEach((l, n) => doc.text(l, MARGEN + COL_VALOR, y0 + n * LINEA));
      y += (lineas - 1) * LINEA + 5.2;
    }
  };
  const miniaturas = (lista: (string | null)[]) => {
    const lado = 28;
    let x = MARGEN + 4;
    espacio(lado + 3);
    for (const img of lista) {
      if (x + lado > MARGEN + ANCHO) { x = MARGEN + 4; y += lado + 2; espacio(lado + 3); }
      doc.setDrawColor(203, 213, 225); doc.rect(x, y, lado, lado);
      if (img) { try { doc.addImage(img, formatoImagen(img), x + 0.5, y + 0.5, lado - 1, lado - 1); } catch { /* imagen dañada: queda el recuadro */ } }
      else { doc.setFontSize(6.5); doc.setTextColor(100, 116, 139); doc.text(T('Adjunto PDF'), x + 3, y + lado / 2); doc.setTextColor(15, 23, 42); }
      x += lado + 3;
    }
    y += lado + 3;
  };

  // Portada
  marca();
  doc.setFillColor(15, 23, 42); doc.rect(0, 0, 210, 60, 'F');
  doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(20);
  doc.text(T(TITULO_INSPECCION), MARGEN, 30);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(12);
  doc.text(T(d.empresa || 'Empresa'), MARGEN, 41);
  doc.setFontSize(10); doc.text(T(`${i.template_name} · ${fechaEc(fechaInspeccion(i))}`), MARGEN, 50);
  doc.setTextColor(15, 23, 42);
  y = 72;
  campos([
    ['Empresa', d.empresa || '—'], ['Sede', d.sede || '—'], ['Área', i.area ?? '—'], ['Plantilla', `${i.template_name} (versión ${i.template_version})`],
    ['Estado', ETIQUETA_ESTADO_INSPECCION[i.status]], ['Fecha programada', fechaEc(i.scheduled_date)], ['Fecha realizada', fechaEc(i.performed_date)],
    ['Responsable', d.responsable || '—'],
  ]);

  // Respuestas por sección
  const resp = new Map(d.respuestas.map(r => [r.item_key, r]));
  let n = 0;
  i.template_snapshot.sections.forEach((s, k) => {
    titulo(`${k + 1}. ${s.title}`);
    s.items.forEach(it => {
      n++;
      const r = resp.get(it.id);
      const fallo = esFalla(it, r);
      texto(`${n}. ${it.text}`, 8.5, { negrita: true, sangria: 2 });
      texto(`Respuesta: ${respuestaTexto(it.response_type, r, it.unit)}`, 8.5, { sangria: 6, color: fallo ? [185, 28, 28] : undefined });
      const regla = textoRegla(it.rule_key, d.cantonSede);
      if (regla) texto(regla, 7.5, { gris: true, sangria: 6 });
      if (it.reference) texto(`Referencia: ${it.reference}`, 7.5, { gris: true, sangria: 6 });
      if (r?.note?.trim()) texto(`Observación: ${r.note.trim()}`, 8, { sangria: 6 });
      const f = d.fotos[it.id] ?? [];
      if (f.length) miniaturas(f);
      y += 1;
    });
  });
  if (!itemsDe(i.template_snapshot).length) texto('La plantilla no tiene ítems.', 8.5, { gris: true });

  titulo('Observaciones generales');
  texto(i.observations?.trim() || 'Sin observaciones.', 8.5, { gris: !i.observations?.trim() });

  titulo('Hallazgos');
  if (!d.hallazgos.length) texto('Sin hallazgos.', 8.5, { gris: true });
  d.hallazgos.forEach((h, k) => {
    texto(`${k + 1}. ${h.description}`, 8.5, { negrita: true, sangria: 2 });
    texto(`Riesgo: ${ETIQUETA_RIESGO[h.risk_level]} · Estado: ${ETIQUETA_ESTADO_HALLAZGO[estadoHallazgo(h, hoy)]} · Fecha límite: ${fechaEc(h.due_date)} · Responsable: ${h.responsible_role || (h.responsible_worker_id ? 'trabajador asignado' : 'sin responsable')}`,
      8, { gris: true, sangria: 6 });
    if (h.corrective_action?.trim()) texto(`Acción correctiva: ${h.corrective_action.trim()}`, 8, { sangria: 6 });
  });

  // Firmas (K6)
  espacio(75);
  titulo('Firmas');
  if (d.metodo) texto(`Método: ${ETIQUETA_METODO_FIRMA[d.metodo]}. Firma el inspector; el responsable del área es opcional.`, 8, { gris: true });
  espacio(52);
  const anchoCol = (ANCHO - 6) / 2; const y0 = y + 1;
  (['inspector', 'responsable'] as RolFirma[]).forEach((rol, k) => {
    const f = d.firmas.find(x => x.rol === rol);
    const x = MARGEN + k * (anchoCol + 6);
    doc.setDrawColor(203, 213, 225); doc.rect(x, y0, anchoCol, 24);
    if (f?.imagen) { try { doc.addImage(f.imagen, 'PNG', x + 2, y0 + 1, anchoCol - 4, 22); } catch { /* imagen dañada */ } }
    else if (f?.archivo) { doc.setFontSize(8); doc.setTextColor(100, 116, 139); doc.text(T('Firmado en el documento adjunto'), x + 3, y0 + 12); doc.setTextColor(15, 23, 42); }
    doc.setFontSize(8.5); doc.setFont('helvetica', 'bold'); doc.text(T(ETIQUETA_ROL_FIRMA[rol] + (rol === 'responsable' ? ' (opcional)' : '')), x, y0 + 28);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    if (f) {
      doc.text(T(`${f.nombre} · C.I. ${f.cedula}`), x, y0 + 32);
      doc.text(T(new Date(f.firmadoEn).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' })), x, y0 + 36);
      doc.setFontSize(6.5); doc.setTextColor(100, 116, 139);
      doc.text(T(`SHA-256 ${f.hash.slice(0, 32)}…`), x, y0 + 40); doc.setTextColor(15, 23, 42);
    } else doc.text(T(rol === 'inspector' ? 'Firma pendiente' : 'Sin firma (opcional)'), x, y0 + 32);
  });
  y = y0 + 46;

  // Encabezado y pie
  const pie = `${TITULO_INSPECCION} · ${i.template_name} · ${fechaEc(fechaInspeccion(i))} · generado ${d.fecha.toLocaleDateString('es-EC')}`;
  const paginas = doc.getNumberOfPages();
  for (let p = 1; p <= paginas; p++) {
    doc.setPage(p);
    if (p > 1) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(100, 116, 139);
      doc.text(T(d.empresa || ''), MARGEN, 10);
      doc.text(T(TITULO_INSPECCION), 210 - MARGEN, 10, { align: 'right' });
      doc.setDrawColor(226, 232, 240); doc.line(MARGEN, 12, 210 - MARGEN, 12);
    }
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(100, 116, 139);
    doc.text(T(pie), MARGEN, 290);
    doc.text(T(`Página ${p} de ${paginas}`), 210 - MARGEN, 290, { align: 'right' });
    doc.setTextColor(15, 23, 42);
  }
  return doc;
}
